import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LessonOrUnitGroup } from "../common/api";
import { Account } from "../common/context/AccountContext";
import { LessonItem, LessonItemLoggingContext } from "./LessonItem";
import { LeftCornerDecoration } from "./LessonItemBody";
import {
  AnalyticsContext,
  SeeMoreAssignmentsButtonTapEvent,
} from "../common/context/AnalyticsContext";
import {
  GENERIC_ITEM_BORDER_RADIUS,
  SURFACE_CARD_RADIUS_LARGE,
} from "../common/constants";
import { SPACE_LG, SPACE_MD, SPACE_XL } from "../common/spacing";
import {
  COLOR_SURFACE_CONTAINER_STRONG,
  COLOR_TEXT_PRIMARY,
  COLOR_WHITE,
} from "../common/colors";
import { ELEVATION_1 } from "../common/elevation";

const CARD_HEADING_FONT_SIZE = 20;
const SEE_MORE_FONT_SIZE = 16;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${SPACE_XL}px;
  border-radius: ${SURFACE_CARD_RADIUS_LARGE}px;
  background-color: ${COLOR_WHITE};
  box-shadow: ${ELEVATION_1};
`;

const Heading = styled.h3`
  margin: 0 0 ${SPACE_LG}px 0;
  color: ${COLOR_TEXT_PRIMARY};
  font-size: ${CARD_HEADING_FONT_SIZE}px;
  font-weight: 700;
  overflow-wrap: break-word;
`;

const Lessons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE_LG}px;
`;

const SeeMoreLink = styled(Link)`
  margin-top: ${SPACE_LG}px;
  padding: ${SPACE_MD}px;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  background-color: ${COLOR_SURFACE_CONTAINER_STRONG};
  color: ${COLOR_TEXT_PRIMARY};
  font-size: ${SEE_MORE_FONT_SIZE}px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;

  :hover {
    color: ${COLOR_TEXT_PRIMARY};
    text-decoration: none;
  }
`;

export const StudentAssignmentGroupCard = ({
  group,
  account,
}: {
  group: LessonOrUnitGroup;
  account: Account;
}) => {
  const { t } = useTranslation();
  const { logEvent } = React.useContext(AnalyticsContext);
  const lessons = group.lessons ?? [];

  return (
    <Card>
      <Heading>{group.title}</Heading>
      <Lessons>
        {lessons.map((lesson) => (
          <LessonItem
            key={`${lesson.id}-${lesson.custom_flashpack_id}`}
            lesson={lesson}
            unitId={null}
            account={account}
            isCompact
            leftCornerDecoration={LeftCornerDecoration.STANDARD}
            loggingContext={LessonItemLoggingContext.StudentAssigned}
          />
        ))}
      </Lessons>
      {group.assignment_info && (
        <SeeMoreLink
          to={`/student-assignments/${group.assignment_info.teacher_id}/`}
          onClick={() => {
            logEvent(new SeeMoreAssignmentsButtonTapEvent());
          }}
        >
          {t("assignments_see_more")}
        </SeeMoreLink>
      )}
    </Card>
  );
};
